import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, Link, useLocation } from 'react-router-dom';
import {
  CButton, CCard, CCardBody, CCardHeader, CCardFooter,
  CForm, CFormInput, CFormSelect, CFormFeedback,
  CTable, CTableHead, CTableRow, CTableHeaderCell, CTableBody,
  CSpinner, CFormCheck
} from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { cilTrash } from '@coreui/icons';
import { apiClient } from 'services/Api';
import ToastNotification from 'components/globals/ToastNotification';
import { triggerAlert } from 'components/globals/AlertNotification';
import { jwtDecode } from 'jwt-decode';

const ROLE_OPTIONS = [
  { value: 'analista', label: 'Analista' },
  { value: 'consultor', label: 'Consultor' },
  { value: 'consultor_senior', label: 'Consultor Sénior' },
  { value: 'gestor_area', label: 'Gestor de Área' },
  { value: 'diretor', label: 'Diretor' },
  { value: 'gerente', label: 'Gerente' },
];

const Department = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [formData, setFormData] = useState(null);
  const [memberships, setMemberships] = useState([]);
  const [editRows, setEditRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [toast, setToast] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const token = localStorage.getItem('access_token');
  const decodedToken = token ? jwtDecode(token) : {};
  const is_manager = !!decodedToken?.is_manager;
  const is_staff = !!decodedToken?.is_staff;
  const canEdit = is_manager || is_staff;
  const isFixedDirection = !!formData?.is_direction;

  const fetchDepartment = async () => {
    try {
      const res = await apiClient.get(`/departments/${id}/`);
      setFormData(res.data);
    } catch (err) {
      triggerAlert('Departamento não encontrado', 'error');
      navigate('/departamentos');
    }
  };

  const fetchMemberships = async () => {
    if (!id) return;
    setLoadingMembers(true);
    try {
      const res = await apiClient.get(`/departments/${id}/memberships/`);
      setMemberships(res.data || []);
      setEditRows(
        (res.data || []).map((m) => ({
          user: m.user?.id ?? m.user,
          role: m.role,
          responsible: !!m.responsible,
          _tmp: crypto.randomUUID(),
        }))
      );
    } catch (err) {
      console.error('Erro a carregar membros do departamento', err);
    } finally {
      setLoadingMembers(false);
    }
  };

  const fetchUsers = async () => {
    setLoadingUsers(true);
    try {
      let response = await apiClient.get('/users/active/');
      setUsers(response.data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  useEffect(() => {
    if (!id) {
      setFormData({});
      setMemberships([]);
      setEditRows([]);
      fetchUsers();
    } else {
      (async () => {
        await fetchDepartment();
        await fetchMemberships();
        await fetchUsers();
      })();
    }
    // eslint-disable-next-line
  }, [id, location.pathname]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const v = value === '' ? null : value;
    setFormData((prev) => ({ ...prev, [name]: v }));
    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const submitDepartment = async (e) => {
    e?.preventDefault?.();
    if (!canEdit) {
      triggerAlert('Sem permissões para editar o departamento.', 'error');
      return;
    }
    setLoading(true);
    try {
      if (id) {
        await apiClient.put(`/departments/${id}/`, formData);
        setToast({ title: 'Departamento', message: 'Departamento atualizado com sucesso!', type: 'success' });
      } else {
        const res = await apiClient.post('/departments/', formData);
        triggerAlert('Departamento criado com sucesso!', 'success');
        navigate(`/departamentos/${res.data.id}`);
        return;
      }
    } catch (error) {
      const errors = error.response?.data;
      let errorMessage = 'Erro ao gravar';
      if (errors && typeof errors === 'object') setFieldErrors(errors);
      setToast({ title: 'Departamento', message: errorMessage, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const addRow = () =>
    setEditRows((rs) => [...rs, { _tmp: crypto.randomUUID(), user: '', role: 'analista', responsible: false }]);

  const removeRow = (tmp) =>
    setEditRows((rs) => rs.filter((r) => r._tmp !== tmp));

  const changeUserId = (tmp, userId) =>
    setEditRows((rs) => rs.map((r) => (r._tmp === tmp ? { ...r, user: userId ? Number(userId) : '' } : r)));

  const changeRole = (tmp, role) =>
    setEditRows((rs) => rs.map((r) => (r._tmp === tmp ? { ...r, role } : r)));

  const saveMemberships = async () => {
    if (!canEdit) {
      triggerAlert('Sem permissões para gravar membros.', 'error');
      return;
    }
    try {
      const payload = editRows
        .filter((r) => r.user && r.role)
        .map(({ user, role, responsible }) => ({ user: Number(user), role, responsible: !!responsible }));
      await apiClient.post(`/departments/${id}/set-memberships/`, payload);
      setToast({ title: 'Departamentos', message: 'Membros/Funções atualizados com sucesso!', type: 'success' });
      await fetchMemberships();
    } catch (err) {
      console.error('Erro ao gravar membros:', err);
      const detail = err?.response?.data?.detail;
      const message =
        typeof detail === 'string'
          ? detail
          : 'Erro ao gravar membros';
      triggerAlert(message, 'error');
    }
  };

  if (formData === null) {
    return (
      <div className="container mt-4">
        <h2>Loading...</h2>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      <ToastNotification toast={toast} setToast={setToast} />

      <div className="mb-3">
        <Link to="/departamentos">&larr; Voltar</Link>
      </div>

      <CCard className="mb-4">
        <CCardHeader><h4>Departamento</h4></CCardHeader>
        <CCardBody>
          <CForm onSubmit={submitDepartment}>
            <div className="mb-3">
              <CFormInput
                type="text"
                label={<strong>Nome</strong>}
                name="name"
                value={formData.name ?? ''}
                onChange={handleInputChange}
                className="mb-1"
                invalid={!!fieldErrors.name}
                disabled={!canEdit || isFixedDirection}
              />
              {fieldErrors.name && (
                <CFormFeedback invalid>{fieldErrors.name[0]}</CFormFeedback>
              )}
              {isFixedDirection && (
                <div className="text-body-secondary small">
                  Este departamento é fixo e não pode ser renomeado.
                </div>
              )}
            </div>
          </CForm>
        </CCardBody>
        <CCardFooter className="d-flex justify-content-end gap-2">
          <CButton color="danger" className="text-white" onClick={() => navigate('/departamentos')}>
            Cancelar
          </CButton>
          {canEdit && !isFixedDirection && (
            <CButton disabled={loading} color="success" className="text-white" type="submit" onClick={submitDepartment}>
              {loading ? <CSpinner size="sm" /> : 'Confirmar'}
            </CButton>
          )}
        </CCardFooter>
      </CCard>

      {!!id && (
        <CCard>
          <CCardHeader><h5>Membros &amp; Funções</h5></CCardHeader>
          <CCardBody>
            {loadingMembers || loadingUsers ? (
              <div className="my-2"><CSpinner size="sm" /> A carregar…</div>
            ) : (
              <>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <div className="text-body-secondary">
                    Substitui a lista completa de membros ao gravar.
                  </div>
                  {canEdit && (
                    <CButton size="sm" color="primary" onClick={addRow}>Adicionar Linha</CButton>
                  )}
                </div>

                <CTable small hover responsive>
                  <CTableHead>
                    <CTableRow>
                      <CTableHeaderCell className="text-center">Colaborador</CTableHeaderCell>
                      <CTableHeaderCell className="text-center">Cargo</CTableHeaderCell>
                      <CTableHeaderCell className="text-center">Responsável</CTableHeaderCell>
                      <CTableHeaderCell className="text-end" style={{ width: 50 }}></CTableHeaderCell>
                    </CTableRow>
                  </CTableHead>
                  <CTableBody>
                    {editRows.map((r) => (
                      <CTableRow key={r._tmp}>
                        <CTableHeaderCell>
                          <CFormSelect
                            value={r.user}
                            onChange={(e) => changeUserId(r._tmp, e.target.value)}
                            disabled={!canEdit}
                          >
                            <option value="">Selecione um utilizador</option>
                            {users.map((u) => (
                              <option key={u.id} value={u.id}>
                                {u.first_name} {u.last_name}
                              </option>
                            ))}
                          </CFormSelect>
                        </CTableHeaderCell>
                        <CTableHeaderCell>
                          <CFormSelect
                            value={r.role}
                            onChange={(e) => changeRole(r._tmp, e.target.value)}
                            disabled={!canEdit}
                          >
                            {ROLE_OPTIONS.map((opt) => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </CFormSelect>
                        </CTableHeaderCell>
                        <CTableHeaderCell className="text-center" style={{ verticalAlign: 'middle' }}>
                          <CFormCheck
                            checked={r.responsible || false}
                            onChange={(e) => {
                              const isResponsible = e.target.checked;
                              setEditRows((rs) =>
                                rs.map((row) =>
                                  row._tmp === r._tmp ? { ...row, responsible: isResponsible } : row
                                )
                              );
                            }}
                            disabled={!canEdit}
                          />
                        </CTableHeaderCell>
                        <CTableHeaderCell className="text-center" style={{ verticalAlign: 'middle' }}>
                          {canEdit && (
                            <CButton color="danger" variant="outline" size="sm" onClick={() => removeRow(r._tmp)}>
                              <CIcon icon={cilTrash} />
                            </CButton>
                          )}
                        </CTableHeaderCell>
                      </CTableRow>
                    ))}
                    {editRows.length === 0 && (
                      <CTableRow>
                        <CTableHeaderCell colSpan={4} className="text-center text-body-secondary">
                          Sem Dados
                        </CTableHeaderCell>
                      </CTableRow>
                    )}
                  </CTableBody>
                </CTable>

                <div className="d-flex justify-content-end mt-3">
                  {canEdit && (
                    <CButton color="success" onClick={saveMemberships}>Gravar Membros</CButton>
                  )}
                </div>
              </>
            )}
          </CCardBody>
          <CCardFooter className="d-flex justify-content-end">
            <Link to={`/departamentos/${id}/avaliacoes`} className="btn btn-link">Gerir/Ver Avaliações do Departamento</Link>
          </CCardFooter>
        </CCard>
      )}
    </div>
  );
};

export default Department;
